import { Element, HISTORY_ACTION_MODE, useEditor } from '@candulabs/craft';
import { css, DuplicateIcon, styled, textEllipsis } from 'canoe';
import { connectWithHammock, ENDPOINTS } from 'hammock';
import { nanoid } from 'nanoid';
import React from 'react';
import { useDispatch } from 'react-redux';

import BlankFlowIcon from '!!react-svg-loader!./assets/blank-dynamic-flow.svg';
import CheckboxIcon from '!!react-svg-loader!./assets/checkbox.svg';
import EmailInputIcon from '!!react-svg-loader!./assets/email-input.svg';
import MultiSelectCardIcon from '!!react-svg-loader!./assets/multiselect-cards.svg';
import SelectInputIcon from '!!react-svg-loader!./assets/select-input.svg';
import SingleSelectCardIcon from '!!react-svg-loader!./assets/singleselect-cards.svg';
import TextInputIcon from '!!react-svg-loader!./assets/text-input.svg';
import TextareaIcon from '!!react-svg-loader!./assets/textarea.svg';
import { DropInBox, DropInLabel, DropInThumbnail } from '@candu-ai/craft/ui';
import {
  FORM_DEFAULT_CHECKBOX_COLUMNS,
  FORM_DEFAULT_CHECKBOX_LABEL,
  FORM_DEFAULT_CHECKBOX_OPTIONS,
  FORM_DEFAULT_EMAIL_INPUT_PLACEHOLDER,
  FORM_DEFAULT_MULTI_SELECT_CARD_LABEL,
  FORM_DEFAULT_SELECT_CARD_COLUMNS,
  FORM_DEFAULT_SELECT_INPUT_LABEL,
  FORM_DEFAULT_SELECT_INPUT_OPTIONS,
  FORM_DEFAULT_SELECT_INPUT_PLACEHOLDER,
  FORM_DEFAULT_SINGLE_SELECT_CARD_LABEL,
  FORM_DEFAULT_TEXT_INPUT_LABEL,
  FORM_DEFAULT_TEXT_INPUT_PLACEHOLDER,
  FORM_DEFAULT_TEXTAREA_LABEL,
  FORM_DEFAULT_TEXTAREA_PLACEHOLDER,
  FORM_FIELD_CONTAINER_STYLES,
  FORM_FIELD_SELECTCARD_STYLES,
} from '@candu-ai/forms/constants';
import { generateFormFieldId } from '@candu-ai/forms/editor-utils';
import { useDefaultVariants } from '@candu-ai/styleguide/application-variants';
import { VariantNode } from '@candu-ai/styleguide/types';
import { toCssSelector } from '@candu-ai/stylesheet/utils';
import { TypographyVariants } from 'craft/constants';
import { generateEventName } from 'craft/containers/Actions';
import { VariantItem } from 'craft/hooks/usePreparedVariants';
import {
  Columns as ColumnsIcon,
  Content as ContentIcon,
  ConditionalView as ConditionalViewIcon,
  IFrame as IFrameIcon,
  Image as ImageIcon,
  LeftImgView as LeftImgViewIcon,
  RightImgView as RightImgViewIcon,
  SingleView as SingleViewIcon,
  StackedView as StackedViewIcon,
  Video as VideoIcon,
} from 'craft/icons';
import { Badge } from 'craft/resolvers/Badge';
import { Button } from 'craft/resolvers/Button';
import { Card } from 'craft/resolvers/Card';
import { CheckListGroup } from 'craft/resolvers/CheckList';
import { CheckMark } from 'craft/resolvers/CheckList/CheckBox';
import {
  Column,
  COLUMN_DEFAULT_STYLES,
  Columns,
  COLUMNS_DEFAULT_STYLES,
} from 'craft/resolvers/Columns';
import { ConditionalView, ConditionalViewOption } from 'craft/resolvers/ConditionalView';
import { EmbedContent } from 'craft/resolvers/EmbedContent';
import { Flow, FlowStep } from 'craft/resolvers/Flow';
import {
  FormCheckboxList,
  FormMultiSelectCard,
  FormMultiSelectCardGroup,
  FormSelectInput,
  FormSingleSelectCard,
  FormSingleSelectCardGroup,
  FormTextarea,
  FormTextInput,
} from 'craft/resolvers/Forms';
import { DEFAULT_HEIGHT, DEFAULT_WIDTH, IFrame } from 'craft/resolvers/IFrame';
import { Image } from 'craft/resolvers/Image';
import { Player } from 'craft/resolvers/Player';
import { RichText } from 'craft/resolvers/RichText';
import { Spacing } from 'craft/resolvers/Spacing';
import { Text } from 'craft/resolvers/Text';
import { constants as typographyStyles, Typography } from 'craft/resolvers/Typography';
import { View, VIEW_DEFAULT_STYLES } from 'craft/resolvers/View';
import { setToolboxTab } from 'craft/state';
import { Action, CATEGORIES, CustomNodeData, HANDLER_TYPES, ToolboxTabs } from 'craft/types';

interface Props {
  variant: VariantNode;
}

/**
 * Layouts
 */
export const SingleViewDropIn = () => (
  <DropInBox id="view" dropIn={<Element is={View} custom={{ css: VIEW_DEFAULT_STYLES }} />}>
    <DropInThumbnail>
      <SingleViewIcon />
    </DropInThumbnail>
    <DropInLabel>Single Layout</DropInLabel>
  </DropInBox>
);

export const ColumnDropIn = () => (
  <DropInBox
    id="column"
    dropIn={
      <Element is={Columns} custom={{ css: COLUMNS_DEFAULT_STYLES }}>
        <Element
          is={Column}
          id={nanoid(6)}
          custom={{
            custom: {
              width: '6',
            },
            css: COLUMN_DEFAULT_STYLES,
          }}
        />
        <Element
          is={Column}
          id={nanoid(6)}
          custom={{
            custom: {
              width: '6',
            },
            css: COLUMN_DEFAULT_STYLES,
          }}
        />
      </Element>
    }
  >
    <DropInThumbnail>
      <ColumnsIcon />
    </DropInThumbnail>
    <DropInLabel>Columns</DropInLabel>
  </DropInBox>
);

export const StackedViewDropIn = () => (
  <DropInBox
    id="stackedView"
    dropIn={
      <Element
        is={View}
        custom={{ css: { ...VIEW_DEFAULT_STYLES, alignItems: 'stretch', padding: 0 } }}
      >
        <Element is={View} custom={{ css: VIEW_DEFAULT_STYLES }} />
        <Element is={View} custom={{ css: VIEW_DEFAULT_STYLES }} />
        <Element is={View} custom={{ css: VIEW_DEFAULT_STYLES }} />
      </Element>
    }
  >
    <DropInThumbnail>
      <StackedViewIcon />
    </DropInThumbnail>
    <DropInLabel>Layout Stack</DropInLabel>
  </DropInBox>
);

export const RightImgViewDropIn = () => (
  <DropInBox
    id="rightImageView"
    dropIn={
      <Element
        is={View}
        custom={{ css: { ...VIEW_DEFAULT_STYLES, flexDirection: 'row', padding: 0 } }}
      >
        <Element is={View} custom={{ css: { ...VIEW_DEFAULT_STYLES, width: '70%' } }} />
        <Element
          is={View}
          custom={{ css: { ...VIEW_DEFAULT_STYLES, width: '30%', alignItems: 'center' } }}
        >
          <Element is={Image} custom={containedImageProps} />
        </Element>
      </Element>
    }
  >
    <DropInThumbnail>
      <RightImgViewIcon />
    </DropInThumbnail>
    <DropInLabel>Right Image</DropInLabel>
  </DropInBox>
);

export const LeftImgViewDropIn = () => (
  <DropInBox
    id="leftImageView"
    dropIn={
      <Element
        is={View}
        custom={{ css: { ...VIEW_DEFAULT_STYLES, flexDirection: 'row', padding: 0 } }}
      >
        <Element
          is={View}
          custom={{ css: { ...VIEW_DEFAULT_STYLES, width: '30%', alignItems: 'center' } }}
        >
          <Element is={Image} custom={containedImageProps} />
        </Element>
        <Element is={View} custom={{ css: { ...VIEW_DEFAULT_STYLES, width: '70%' } }} />
      </Element>
    }
  >
    <DropInThumbnail>
      <LeftImgViewIcon />
    </DropInThumbnail>
    <DropInLabel>Left Image</DropInLabel>
  </DropInBox>
);

/**
 * Typography
 */
const getTypographyClass = (conditions: VariantItem['conditions']) => {
  const { variant, variation } = conditions;
  const typographyVariant = {
    variant: variation || variant || 'base',
  };
  return toCssSelector('typography', {
    variant: typographyVariant,
    output: 'html',
  });
};

const getTypographyName = (name: string): string => {
  const hasSpace = name.includes(' ');
  if (hasSpace) {
    const [first, second] = name.split(' ');
    return `${first[0]}${second[0]}`;
  }

  return name.substr(0, 2);
};

export const TypographyDropIn = ({ variant }: Props) => {
  const conditionVariant = variant.conditions;

  const defaultCss = (typographyStyles as any)[conditionVariant.variant] || typographyStyles.P;
  return (
    <DropInBox
      key={`typography-${variant.name}`}
      id={`typography-${variant.name}`}
      dropIn={
        <Element
          is={Typography}
          custom={{
            custom: variant.conditions,
            css: { ...defaultCss },
          }}
        >
          <Text childrenString={variant.name} />
        </Element>
      }
    >
      <DropInThumbnail alignContent="end" className={getTypographyClass(variant.conditions)}>
        {getTypographyName(variant.name)}
      </DropInThumbnail>
      <DropInLabel title={variant.name}>{variant.name}</DropInLabel>
    </DropInBox>
  );
};

/**
 * Code
 */
const CodeBrackets = styled.span`
  ${({ theme }) => css`
    font-size: 10px;
    color: ${theme.colors.White};
    font-weight: ${theme.fontWeights.bold};
    padding: ${theme.spaces.xs};
    background-color: ${theme.colors.OnyxGrayD10};
    border-radius: ${theme.borderRadius.small};
    display: flex;
    align-items: center;
    gap: ${theme.spaces.s};
  `}
`;

export const CodeDropIn = () => {
  const { actions } = useEditor();

  return (
    <DropInBox
      id="code"
      dropIn={
        <Element
          is={View}
          custom={{
            css: {
              ...VIEW_DEFAULT_STYLES,
              alignItems: 'stretch',
              paddingTop: 0,
              paddingRight: 0,
              backgroundColor: '#333745',
              borderRadius: '4px',
            },
          }}
        >
          <Element is={RichText} hasCopy>
            <Element
              is={Typography}
              custom={{
                custom: { variant: TypographyVariants.P, variation: 'base' },
                css: {
                  ...typographyStyles.P,
                  fontSize: '13px',
                  paddingTop: 8,
                  paddingRight: 40,
                  paddingBottom: 0,
                  color: '#FFFFFF',
                },
              }}
            >
              <Text childrenString="console.log('hello');" />
            </Element>
          </Element>
        </Element>
      }
      onDropIn={(node) => {
        const rteNodeId = node.nodes[node.rootNodeId].data.nodes[0];

        // add copy action to new RichText node
        actions.setCustom(
          rteNodeId,
          (custom: CustomNodeData) => {
            // eslint-disable-next-line no-param-reassign
            custom.actions = [
              {
                category: CATEGORIES.RICHTEXT,
                eventName: `RichText. Copy Text. RTE. ${rteNodeId}`,
              } as Action,
            ];
          },
          HISTORY_ACTION_MODE.MERGE,
        );
      }}
    >
      <DropInThumbnail alignContent="end">
        <CodeBrackets>
          {'</>'} <DuplicateIcon color="White" size="xs" />
        </CodeBrackets>
      </DropInThumbnail>
      <DropInLabel>Code snippet</DropInLabel>
    </DropInBox>
  );
};

/**
 * Button
 */

const ButtonVariant = styled.div`
  width: 81px;
  min-height: 22px;
  max-height: 22px;
  font-size: 9px !important;
  line-height: 20px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  cursor: grab !important;
  user-select: none;
  opacity: 1;
`;

export const ButtonDropIn = ({ variant }: Props) => {
  debugger;
  const { color, outline, variant: variantId } = variant.conditions;
  const colorMatchedVariant = color === 'primary' ? 'base' : color;
  const appearanceStyle = outline === 'light' ? '_light' : '';
  const buttonVariant = {
    variant: variantId || `${colorMatchedVariant}${appearanceStyle}` || 'base',
  };

  const sssClassname = toCssSelector('button', {
    variant: buttonVariant,
    output: 'html',
  });
  return (
    <DropInBox
      id={`button-${variant.name}`}
      key={`button-${variant.name}`}
      dropIn={
        <Element
          is={Button}
          color={variant.conditions.color}
          outline={variant.conditions.outline}
          variant={variant.conditions.variant}
          childrenString="Change Me"
        />
      }
    >
      <DropInThumbnail>
        <ButtonVariant className={sssClassname}>Button</ButtonVariant>
      </DropInThumbnail>
      <DropInLabel>{variant.name}</DropInLabel>
    </DropInBox>
  );
};

/**
 * Media
 */
const defaultImageProps = {
  css: {
    maxWidth: '100%',
    maxHeight: 'none',
  },
};

const containedImageProps = {
  css: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
};

export const ImageDropIn = () => (
  <DropInBox id="image" dropIn={<Element is={Image} custom={defaultImageProps} />}>
    <DropInThumbnail>
      <ImageIcon />
    </DropInThumbnail>
    <DropInLabel>Image</DropInLabel>
  </DropInBox>
);

export const PlayerDropIn = () => (
  <DropInBox id="player" dropIn={<Player />}>
    <DropInThumbnail>
      <VideoIcon />
    </DropInThumbnail>
    <DropInLabel>Player</DropInLabel>
  </DropInBox>
);

/**
 * Embeds
 */
export const IFrameDropIn = () => (
  <DropInBox
    id="iframe"
    dropIn={<Element is={IFrame} width={DEFAULT_WIDTH} height={DEFAULT_HEIGHT} />}
  >
    <DropInThumbnail>
      <IFrameIcon />
    </DropInThumbnail>
    <DropInLabel>iFrame</DropInLabel>
  </DropInBox>
);

export const ContentDropIn = () => (
  <DropInBox
    id="content"
    dropIn={<Element is={EmbedContent} custom={{ css: { width: '100%' } }} />}
  >
    <DropInThumbnail>
      <ContentIcon />
    </DropInThumbnail>
    <DropInLabel>Candu Content</DropInLabel>
  </DropInBox>
);

export const ConditionalViewDropIn = () => {
  const dispatch = useDispatch();

  return (
    <DropInBox
      id="conditional-view"
      dropIn={
        <Element is={ConditionalView}>
          <Element is={ConditionalViewOption} name="Everyone" />
        </Element>
      }
      onDropIn={() => dispatch(setToolboxTab(ToolboxTabs.Component))}
    >
      <DropInThumbnail>
        <ConditionalViewIcon />
      </DropInThumbnail>
      <DropInLabel>Conditional View</DropInLabel>
    </DropInBox>
  );
};

/**
 * Badges
 */

const BadgeVariant = styled.div`
  width: 78px;
  min-height: 28px;
  max-height: 28px;
  font-size: 10px !important;
  line-height: 26px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  cursor: grab !important;
  user-select: none;
  opacity: 1;
`;

export const BadgeDropIn = ({ variant }: Props) => {
  const { color, outline, variant: variantId } = variant.conditions;
  const colorMatchedVariant = color === 'primary' ? 'base' : color;
  const appearanceStyle = outline === 'light' ? '_light' : '';
  const buttonVariant = {
    variant: variantId || `${colorMatchedVariant}${appearanceStyle}` || 'base',
  };

  const sssClassname = toCssSelector('badge', {
    variant: buttonVariant,
    output: 'html',
  });
  return (
    <DropInBox
      key={`badge-${variant.name}`}
      id={`badge-${variant.name}`}
      dropIn={
        <Element
          is={Badge}
          color={color as string}
          outline={outline}
          variant={variantId}
          childrenString="Badge"
        />
      }
    >
      <DropInThumbnail>
        <BadgeVariant className={sssClassname}>Badge</BadgeVariant>
      </DropInThumbnail>
      <DropInLabel>{variant.name}</DropInLabel>
    </DropInBox>
  );
};

/**
 * Divider
 */
const StyledHr = styled.hr`
  ${({ theme }) => css`
    width: 100%;
    height: 1px;
    background-color: ${theme.colors.GlitterGrayL10};
    border: none;
  `}
`;

export const DividerDropIn = () => (
  <DropInBox
    id="divider"
    dropIn={<Element is={Spacing} custom={{ css: { padding: 8, alignSelf: 'stretch' } }} />}
  >
    <DropInThumbnail>
      <StyledHr />
    </DropInThumbnail>
    <DropInLabel>Divider</DropInLabel>
  </DropInBox>
);

/**
 * Dynamic Blocks
 */
export const DynamicFlowDropIn = () => {
  const dispatch = useDispatch();
  return (
    <DropInBox
      id="flow"
      dropIn={
        <Element is={Flow} stateId={nanoid(6)}>
          <Element is={FlowStep} stepId={nanoid(6)} />
          <Element is={FlowStep} stepId={nanoid(6)} />
        </Element>
      }
      onDropIn={() => dispatch(setToolboxTab(ToolboxTabs.Component))}
    >
      <DropInThumbnail>
        <BlankFlowIcon />
      </DropInThumbnail>
      <DropInLabel>Empty Flow</DropInLabel>
    </DropInBox>
  );
};

/**
 * Checklists
 */
const CheckList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Checkbox = styled.div`
  transform: scale(0.5);
  flex-shrink: 0;
  margin: 1px !important;
`;

const CheckboxItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-grow: 1;
  box-sizing: border-box;
`;

const CheckboxLabel = styled.div`
  font-size: 8px;
  ${textEllipsis}
`;

const ProgressBarContainer = styled.div`
  height: 2px !important;
  margin-bottom: 5px !important;
`;

const ProgressBar = styled.div`
  width: 50%;
`;

const CheckListPreview = ({ variant, name }: { variant?: any; name: string }) => {
  const checkBoxClassName = toCssSelector('checklist', {
    variant,
    className: 'checkbox',
    output: 'html',
  });

  const checkBoxCheckedClassName = toCssSelector('checklist', {
    variant,
    className: 'checkboxChecked',
    output: 'html',
  });
  return (
    <CheckList
      className={toCssSelector('checklist', {
        variant,
        output: 'html',
      })}
    >
      <ProgressBarContainer
        className={toCssSelector('checklist', {
          variant,
          className: 'progressBarContainer',
          output: 'html',
        })}
      >
        <ProgressBar
          className={toCssSelector('checklist', {
            variant,
            className: 'progressBar',
            output: 'html',
          })}
        />
      </ProgressBarContainer>

      <CheckboxItem
        className={toCssSelector('checklist', {
          variant,
          className: 'item',
          output: 'html',
        })}
      >
        <Checkbox className={`${checkBoxClassName} ${checkBoxCheckedClassName}`}>
          <CheckMark />
        </Checkbox>
        <CheckboxLabel>{name}</CheckboxLabel>
      </CheckboxItem>
    </CheckList>
  );
};

export const ChecklistDropIn = ({ variant }: Props) => (
  <DropInBox
    id="checklist"
    key={`checklist-${variant.name}`}
    dropIn={
      <Element is={CheckListGroup} variant={variant.conditions?.variant} title={variant.name} />
    }
  >
    <DropInThumbnail>
      <CheckListPreview variant={variant.conditions} name={variant.name} />
    </DropInThumbnail>
    <DropInLabel>{variant.name}</DropInLabel>
  </DropInBox>
);

/**
 * Cards
 */
const CardTitle = styled.h4`
  font-size: 10px !important;
  line-height: 12px !important;
  margin-bottom: 4px !important;
  ${textEllipsis}
`;

const CardContent = styled.p`
  font-size: 7px !important;
  line-height: 10px !important;
  white-space: pre-wrap;
`;

const CardSampleContent = ({ name }: { name: string }) => {
  return (
    <>
      <CardTitle
        className={toCssSelector('typography', {
          variant: { variant: TypographyVariants.H4 },
          output: 'html',
        })}
      >
        {name}
      </CardTitle>
      <CardContent
        className={toCssSelector('typography', {
          variant: { variant: 'base' },
          output: 'html',
        })}
      >
        This is a preview of how the cards look.
      </CardContent>
    </>
  );
};

const CardContainer = styled.div`
  width: 95%;
  height: 50px;
  display: flex;
  margin: 2px auto 0;
`;

const StyledCard = styled.div`
  flex-grow: 0;
  width: 100%;
  margin: 0;
  padding: 4px !important;
  position: relative;
  cursor: grab !important;
`;

export const CardDropIn = ({ variant }: Props) => {
  return (
    <DropInBox
      id={`card-${variant.name}`}
      key={`card-${variant.name}`}
      dropIn={
        <Element
          is={Card}
          variant={variant.conditions?.variant}
          custom={{
            css: {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
        />
      }
    >
      <DropInThumbnail alignContent="start">
        <CardContainer>
          <StyledCard
            className={toCssSelector('card', {
              variant: { variant: variant.conditions.variant || 'base' },
              output: 'html',
            })}
          >
            <CardSampleContent name={variant.name} />
          </StyledCard>
        </CardContainer>
      </DropInThumbnail>
      <DropInLabel>{variant.name}</DropInLabel>
    </DropInBox>
  );
};

/**
 * Other: Template Blocks
 */

interface TemplateDropInProps {
  editorDocument: any;
  thumbnail: JSX.Element;
  label: string;
}

const TemplateDropInImpl = ({ editorDocument, thumbnail, label }: TemplateDropInProps) => (
  <DropInBox id={`blockTemplate-${label}`} contentDocument={editorDocument.document} noStyles>
    <DropInThumbnail>{thumbnail}</DropInThumbnail>
    <DropInLabel>{label}</DropInLabel>
  </DropInBox>
);

export const TemplateDropInBox = connectWithHammock(({ templateId }) => ({
  resourceName: 'editorDocument',
  endpoint: ENDPOINTS.templateDocumentsEditor,
  id: templateId,
}))(TemplateDropInImpl);

/**
 * Forms
 */

export const FormTextAreaDropIn = ({ disabled }: { disabled?: boolean }) => (
  <DropInBox
    id="formTextarea"
    dropIn={
      <Element
        is={FormTextarea}
        formInputId={generateFormFieldId()}
        label={FORM_DEFAULT_TEXTAREA_LABEL}
        placeholder={FORM_DEFAULT_TEXTAREA_PLACEHOLDER}
        showLabel
        custom={{ css: FORM_FIELD_CONTAINER_STYLES }}
        validation={[{ type: 'required', message: 'Required' }]}
      />
    }
    disabled={disabled}
    noStyles={disabled}
  >
    <DropInThumbnail>
      <TextareaIcon />
    </DropInThumbnail>
    <DropInLabel>Text area</DropInLabel>
  </DropInBox>
);

export const FormTextInputDropIn = ({ disabled }: { disabled?: boolean }) => (
  <DropInBox
    id="formTextInput"
    dropIn={
      <Element
        is={FormTextInput}
        type="text"
        formInputId={generateFormFieldId()}
        label={FORM_DEFAULT_TEXT_INPUT_LABEL}
        placeholder={FORM_DEFAULT_TEXT_INPUT_PLACEHOLDER}
        showLabel
        custom={{ css: FORM_FIELD_CONTAINER_STYLES }}
        validation={[{ type: 'required', message: 'Required' }]}
      />
    }
    disabled={disabled}
    noStyles={disabled}
  >
    <DropInThumbnail>
      <TextInputIcon />
    </DropInThumbnail>
    <DropInLabel>Text input</DropInLabel>
  </DropInBox>
);

export const FormSelectInputDropIn = ({ disabled }: { disabled?: boolean }) => (
  <DropInBox
    id="formSelectInput"
    dropIn={
      <Element
        is={FormSelectInput}
        formInputId={generateFormFieldId()}
        label={FORM_DEFAULT_SELECT_INPUT_LABEL}
        placeholder={FORM_DEFAULT_SELECT_INPUT_PLACEHOLDER}
        options={FORM_DEFAULT_SELECT_INPUT_OPTIONS}
        showLabel
        custom={{ css: FORM_FIELD_CONTAINER_STYLES }}
        validation={[{ type: 'required', message: 'Required' }]}
      />
    }
    disabled={disabled}
    noStyles={disabled}
  >
    <DropInThumbnail>
      <SelectInputIcon />
    </DropInThumbnail>
    <DropInLabel>Select input</DropInLabel>
  </DropInBox>
);

export const FormEmailInputDropIn = ({ disabled }: { disabled?: boolean }) => (
  <DropInBox
    id="formEmailInput"
    dropIn={
      <Element
        is={FormTextInput}
        formInputId={generateFormFieldId()}
        type="email"
        label={FORM_DEFAULT_TEXT_INPUT_LABEL}
        placeholder={FORM_DEFAULT_EMAIL_INPUT_PLACEHOLDER}
        showLabel
        custom={{ css: FORM_FIELD_CONTAINER_STYLES }}
        validation={[
          { type: 'email', message: 'Invalid email' },
          { type: 'required', message: 'Required' },
        ]}
      />
    }
    disabled={disabled}
    noStyles={disabled}
  >
    <DropInThumbnail>
      <EmailInputIcon />
    </DropInThumbnail>
    <DropInLabel>Email input</DropInLabel>
  </DropInBox>
);

export const FormCheckboxDropIn = ({ disabled }: { disabled?: boolean }) => (
  <DropInBox
    id="formCheckbox"
    dropIn={
      <Element
        is={FormCheckboxList}
        formInputId={generateFormFieldId()}
        label={FORM_DEFAULT_CHECKBOX_LABEL}
        options={FORM_DEFAULT_CHECKBOX_OPTIONS}
        columns={FORM_DEFAULT_CHECKBOX_COLUMNS}
        showLabel
        custom={{ css: FORM_FIELD_CONTAINER_STYLES }}
        validation={[{ type: 'required', message: 'Required' }]}
      />
    }
    disabled={disabled}
    noStyles={disabled}
  >
    <DropInThumbnail>
      <CheckboxIcon />
    </DropInThumbnail>
    <DropInLabel>Checkboxes</DropInLabel>
  </DropInBox>
);

export const FormMultiSelectCardsDropIn = ({ disabled }: { disabled?: boolean }) => {
  return (
    <DropInBox
      id="formMultiSelectCards"
      dropIn={
        <Element
          is={FormMultiSelectCardGroup}
          formInputId={generateFormFieldId()}
          label={FORM_DEFAULT_MULTI_SELECT_CARD_LABEL}
          columns={FORM_DEFAULT_SELECT_CARD_COLUMNS}
          showLabel
          custom={{ css: FORM_FIELD_CONTAINER_STYLES }}
          validation={[{ type: 'required', message: 'Required' }]}
        >
          <Element
            is={FormMultiSelectCard}
            value="card-1"
            custom={{ css: FORM_FIELD_SELECTCARD_STYLES }}
          />
          <Element
            is={FormMultiSelectCard}
            value="card-2"
            custom={{ css: FORM_FIELD_SELECTCARD_STYLES }}
          />
          <Element
            is={FormMultiSelectCard}
            value="card-3"
            custom={{ css: FORM_FIELD_SELECTCARD_STYLES }}
          />
          <Element
            is={FormMultiSelectCard}
            value="card-4"
            custom={{ css: FORM_FIELD_SELECTCARD_STYLES }}
          />
        </Element>
      }
      disabled={disabled}
      noStyles={disabled}
    >
      <DropInThumbnail>
        <MultiSelectCardIcon />
      </DropInThumbnail>
      <DropInLabel>Multi-select cards</DropInLabel>
    </DropInBox>
  );
};

export const FormSingleSelectCardsDropIn = ({ disabled }: { disabled?: boolean }) => {
  return (
    <DropInBox
      id="formSingleSelectCards"
      dropIn={
        <Element
          is={FormSingleSelectCardGroup}
          formInputId={generateFormFieldId()}
          label={FORM_DEFAULT_SINGLE_SELECT_CARD_LABEL}
          columns={FORM_DEFAULT_SELECT_CARD_COLUMNS}
          showLabel
          custom={{ css: FORM_FIELD_CONTAINER_STYLES }}
          validation={[{ type: 'required', message: 'Required' }]}
        >
          <Element
            is={FormSingleSelectCard}
            value="card-1"
            custom={{ css: FORM_FIELD_SELECTCARD_STYLES }}
          />
          <Element
            is={FormSingleSelectCard}
            value="card-2"
            custom={{ css: FORM_FIELD_SELECTCARD_STYLES }}
          />
          <Element
            is={FormSingleSelectCard}
            value="card-3"
            custom={{ css: FORM_FIELD_SELECTCARD_STYLES }}
          />
          <Element
            is={FormSingleSelectCard}
            value="card-4"
            custom={{ css: FORM_FIELD_SELECTCARD_STYLES }}
          />
        </Element>
      }
      disabled={disabled}
      noStyles={disabled}
    >
      <DropInThumbnail>
        <SingleSelectCardIcon />
      </DropInThumbnail>
      <DropInLabel>Single-select cards</DropInLabel>
    </DropInBox>
  );
};

const getBaseVariant = (variants?: any) => {
  if (!variants) {
    return { variantId: 'base', variant: undefined };
  }

  if (variants.base) {
    return { variantId: 'base', variant: variants.base };
  }

  const variantId = Object.keys(variants)[0];
  return { variantId, variant: variants[variantId] };
};

export const FormSubmitButtonDropIn = ({ disabled }: { disabled?: boolean }) => {
  const { data } = useDefaultVariants();

  const { variants } = data || {};

  const { variantId } = getBaseVariant(variants?.Button);

  const buttonVariant = {
    variant: variantId || 'base',
  };

  const sssClassname = toCssSelector('button', {
    variant: buttonVariant,
    output: 'html',
  });

  return (
    <DropInBox
      id="formSubmitButton"
      key="button-submit-button"
      dropIn={
        <Element
          is={Button}
          variant={variantId}
          childrenString="Submit"
          custom={{
            actions: [
              {
                trigger: 'OnClick',
                category: 'Native',
                eventName: generateEventName(HANDLER_TYPES.FORM_SUBMIT),
                handler: {
                  type: HANDLER_TYPES.FORM_SUBMIT,
                },
              },
            ],
          }}
        />
      }
      disabled={disabled}
      noStyles={disabled}
    >
      <DropInThumbnail>
        <ButtonVariant className={sssClassname}>Button</ButtonVariant>
      </DropInThumbnail>
      <DropInLabel>Submit Button</DropInLabel>
    </DropInBox>
  );
};
